import { InvoiceInformation, PaymentInformation } from '@ravago/shared/page-data/models/features';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

type CheckoutAdditionalDetailLabel = 'prices.delivery-term' | 'prices.payment-term-01' | 'prices.payment-term-02' | 'prices.invoicing-entity';

interface CheckoutAdditionalDetail {
   label: CheckoutAdditionalDetailLabel;
   value: string;
}

interface Props {
   deliveryTerm?: string;
   invoicingInformation?: InvoiceInformation;
   paymentInformation?: PaymentInformation;
}

export const getCheckoutAdditionalDetails = ({
   deliveryTerm,
   invoicingInformation,
   paymentInformation
}: Props): CheckoutAdditionalDetail[] | null => {
   const details: CheckoutAdditionalDetail[] = [];

   if (deliveryTerm) {
      details.push({ label: 'prices.delivery-term', value: deliveryTerm });
   }
   if (paymentInformation?.paymentTerms) {
      details.push({ label: 'prices.payment-term-01', value: paymentInformation.paymentTerms });
   }
   if (paymentInformation?.paymentTerms2) {
      details.push({ label: 'prices.payment-term-02', value: paymentInformation.paymentTerms2 });
   }
   if (invoicingInformation?.invoicingEntity) {
      details.push({ label: 'prices.invoicing-entity', value: invoicingInformation.invoicingEntity });
   }

   return details.length > 0 ? details : null;
};

export const CheckoutAdditionalDetails = (props: Props) => {
   const translate = useTranslations();
   const details = getCheckoutAdditionalDetails(props);

   if (!details) {
      return null;
   }

   return (
      <Box direction="column">
         <Typography component="h5" variant="body-highlight">
            {translate('shopping-cart.checkout.additional-details')}
         </Typography>
         <Box direction="column" gap="md">
            {details.map(({ label, value }) => (
               <Box key={label} gap="sm" items="start">
                  <Box width="200px" minWidth="min(200px, 40%)">
                     <Typography variant="body-highlight">{translate(label)}</Typography>
                  </Box>
                  <Box grow={1} wordBreak="words">
                     <Typography variant="body">{value}</Typography>
                  </Box>
               </Box>
            ))}
         </Box>
      </Box>
   );
};
